import { Link } from "@/lib/router";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@/lib/rpc";
import { Check, Sparkles } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { PLANS, type PlanId } from "@/lib/catalog";
import { requestUpgrade } from "@/lib/access.functions";
import { useAccess } from "@/lib/use-access";
import { describeError } from "@/lib/api-error";
import { cn } from "@/lib/utils";

const inr = (n: number) => `₹${n.toLocaleString("en-IN")}`;

const FREE_FEATURES = [
  "Prakrit alphabet (वर्णमाला)",
  "Present tense (वर्तमान काल) lessons",
  "3 games and 3 sentence challenges",
  "Full dictionary — 55,000 words",
];
const MEMBER_FEATURES = [
  "Every grammar topic — past, future, imperative, participles, cases",
  "All lessons on the learning path",
  "All 29 games and every sentence challenge",
  "Reading passages and the full verb lab",
  "Bhashini Sanskrit-voice pronunciation",
  "XP, streaks and badges",
];

/**
 * Plan cards. Guests are sent to sign up; signed-in learners send an upgrade
 * request that an admin activates (online payment is not connected yet).
 */
export function PricingCards({
  showFree = true,
  compact = false,
}: {
  showFree?: boolean;
  compact?: boolean;
}) {
  const access = useAccess();
  const send = useServerFn(requestUpgrade);
  const [sent, setSent] = useState<PlanId | null>(null);
  const request = useMutation({
    mutationFn: (plan: PlanId) => send({ data: { plan } }),
    onSuccess: (_r, plan) => setSent(plan),
  });

  return (
    <div className="space-y-4">
      <div className={cn("grid gap-4", showFree ? "md:grid-cols-3" : "sm:grid-cols-2")}>
        {showFree && (
          <article className="bubble flex flex-col p-6">
            <h3 className="text-xl font-extrabold">Free</h3>
            <p className="mt-2 text-4xl font-extrabold">{inr(0)}</p>
            <p className="text-sm text-muted-foreground">Forever, no card needed</p>
            {!compact && <Features items={FREE_FEATURES} />}
            <div className="mt-auto pt-6">
              {access.signedIn ? (
                <Button variant="outline" className="w-full" disabled>
                  {access.full ? "Included" : "Your current plan"}
                </Button>
              ) : (
                <Button asChild variant="outline" className="w-full">
                  <Link to="/signup">Start free</Link>
                </Button>
              )}
            </div>
          </article>
        )}
        {PLANS.map((plan) => {
          const best = "best" in plan && plan.best;
          return (
            <article
              key={plan.id}
              className={cn(
                "bubble relative flex flex-col p-6",
                best && "bg-candy text-white shadow-pop ring-4 ring-[#c4b4ff]/60",
              )}
            >
              {best && (
                <span className="absolute -top-3 right-5 inline-flex items-center gap-1 rounded-full bg-[#ffb627] px-3 py-1 text-xs font-extrabold text-[#4a2f00]">
                  <Sparkles className="size-3.5" /> Best value
                </span>
              )}
              <h3 className="text-xl font-extrabold">{plan.name}</h3>
              <p className="mt-2 text-4xl font-extrabold">
                {inr(plan.price)}
                <span
                  className={cn(
                    "text-base font-bold",
                    best ? "text-white/80" : "text-muted-foreground",
                  )}
                >
                  {" "}
                  / {plan.period}
                </span>
              </p>
              <p className={cn("text-sm", best ? "text-white/85" : "text-muted-foreground")}>
                {plan.note}
              </p>
              {!compact && <Features items={MEMBER_FEATURES} light={best} />}
              <div className="mt-auto pt-6">
                {access.full ? (
                  <Button variant={best ? "secondary" : "outline"} className="w-full" disabled>
                    {access.staff ? "Staff — full access" : "You're a member"}
                  </Button>
                ) : !access.signedIn ? (
                  <Button asChild variant={best ? "secondary" : "default"} className="w-full">
                    <Link to="/signup">Get {plan.name}</Link>
                  </Button>
                ) : (
                  <Button
                    variant={best ? "secondary" : "default"}
                    className="w-full"
                    disabled={request.isPending || sent !== null}
                    onClick={() => request.mutate(plan.id)}
                  >
                    {sent === plan.id ? "Request sent ✓" : `Get ${plan.name}`}
                  </Button>
                )}
              </div>
            </article>
          );
        })}
      </div>
      {sent && (
        <p className="rounded-2xl bg-[#e3f9ef] p-4 text-sm font-semibold text-[#0d7552]">
          Thanks! Online payment is coming soon. We've noted your {sent} plan request and an admin
          will activate your membership shortly.
        </p>
      )}
      {request.error && (
        <p className="text-sm font-semibold text-destructive">{describeError(request.error)}</p>
      )}
    </div>
  );
}

function Features({ items, light = false }: { items: readonly string[]; light?: boolean }) {
  return (
    <ul className="mt-5 space-y-2 text-sm">
      {items.map((f) => (
        <li key={f} className="flex gap-2">
          <Check
            className={cn("mt-0.5 size-4 shrink-0", light ? "text-white" : "text-[#0d7552]")}
            strokeWidth={3}
          />
          <span>{f}</span>
        </li>
      ))}
    </ul>
  );
}

/** Small, inline upgrade prompt for locked screens — never a popup. */
export function UpgradeInline() {
  const access = useAccess();
  if (access.loading || access.full) return null;
  return (
    <section className="mx-auto mt-6 max-w-4xl">
      <p className="mb-3 text-center text-sm font-bold text-muted-foreground">
        {access.signedIn
          ? "Unlock everything with a membership"
          : "Create a free account, then upgrade any time"}
      </p>
      <PricingCards showFree={false} compact />
      <p className="mt-3 text-center text-sm">
        <Link to="/pricing" className="font-bold text-primary underline-offset-4 hover:underline">
          Compare plans
        </Link>
      </p>
    </section>
  );
}
